<!-- ══ Loading ════════════════════════════════════════════════════════════════ -->
@if (loading) {
  <div class="vs-loading">
    <div class="vs-spinner"></div>
  </div>
}

@if (!loading) {
  <!-- ── Hero ───────────────────────────────────────────────────────────────── -->
  <div class="vs-hero">
    <div class="vs-hero__crown"><i class="fas fa-crown"></i></div>
    <h1 class="vs-hero__title">חבילות <span>VIP</span></h1>
    <p class="vs-hero__sub">שדרג את חשבונך וקבל יתרונות בלעדיים</p>
    @if (isVipNow) {
      <div class="vs-active-banner">
        <i class="fas fa-check-circle"></i>
        <span>
          המנוי שלך פעיל — תודה שאתה חלק ממשפחת ה-VIP!
          @if (paidUntil) {
            <span class="vs-active-banner__renews">בתוקף עד {{ paidUntil | date:'dd/MM/yyyy' }}</span>
          }
        </span>
        <button class="vs-btn vs-btn--outline-green" (click)="goToDashboard()">
          <i class="mdi mdi-view-dashboard"></i> לוח בקרה
        </button>
      </div>
    }
  </div>
  <!-- ── Renewal (SCRUM-289) ─────────────────────────────────────────────────── -->
  @if (isVipNow && renewalOpen) {
    <div class="vs-renewal-banner" role="status">
      <i class="fas fa-redo-alt"></i>
      <span>
        המנוי שלך מסתיים ב-{{ paidUntil | date:'dd/MM/yyyy' }}. אפשר לחדש עכשיו את אותו מסלול — חודשי או שנתי, לבחירתך.
        החידוש יתחיל בתום התקופה הנוכחית, כך שלא תאבד אף יום ששולם.
      </span>
    </div>
  } @else if (isVipNow && renewalOpensAt) {
    <div class="vs-renewal-note">
      חידוש המסלול ייפתח {{ renewalWindowDays }} ימים לפני סיום המנוי, ב-{{ renewalOpensAt | date:'dd/MM/yyyy' }}. בינתיים אפשר לשדרג למסלול גבוה יותר.
    </div>
  }
  @if (actionError) {
    <div class="vs-action-error" role="alert">
      <i class="fas fa-exclamation-circle"></i>
      <span>{{ actionError }}</span>
      <button type="button" (click)="actionError = ''" aria-label="סגירה">&times;</button>
    </div>
  }
  <!-- ── Billing toggle ─────────────────────────────────────────────────────── -->
  @if (tiers.length > 0) {
    <div class="vs-billing-toggle">
      <button
        class="vs-billing-toggle__btn"
        [class.active]="billingPeriod === 'MONTHLY'"
        (click)="toggleBilling('MONTHLY')"
      >חודשי</button>
      <button
        class="vs-billing-toggle__btn"
        [class.active]="billingPeriod === 'YEARLY'"
        (click)="toggleBilling('YEARLY')"
        >
        שנתי
        @if (tiers[0] && savingPct(tiers[0]) > 0) {
          <span class="vs-billing-toggle__save">
            חסוך {{ savingPct(tiers[0]) }}%
          </span>
        }
      </button>
    </div>
  }
  <!-- ── Plans grid ─────────────────────────────────────────────────────────── -->
  @if (tiers.length > 0) {
    <div class="vs-plans">
      @for (tier of tiers; track tier) {
        <div
          class="vs-card"
          [class.vs-card--popular]="tier.popular"
          [class.vs-card--requested]="tier.sortOrder === highlightedTierSortOrder"
          [ngClass]="'vs-card--' + tier.tierClass"
          >
          <!-- Current plan badge (SCRUM-120) — takes priority over "popular" on
               the same card; a user's own plan is more relevant to them than
               a marketing badge. -->
          @if (isOwnTier(tier)) {
            <div class="vs-card__current-badge">
              <i class="fas fa-check-circle"></i> התוכנית הנוכחית שלך
            </div>
          } @else if (tier.popular) {
            <div class="vs-card__popular-badge">
              <i class="fas fa-star"></i> הכי פופולרי
            </div>
          }
          <!-- No plan for selected period -->
          @if (!activePlan(tier)) {
            <div class="vs-card__tier-icon"><i class="fas fa-crown"></i></div>
            <div class="vs-card__tier-label">{{ tier.tierLabel }}</div>
            <h3 class="vs-card__title">{{ (tier.monthly || tier.yearly)?.name }}</h3>
            <p class="vs-card__unavailable">לא זמין לתקופה זו</p>
          }
          <!-- Plan available for selected period -->
          @if (activePlan(tier); as plan) {
            <div class="vs-card__tier-icon"><i class="fas fa-crown"></i></div>
            <div class="vs-card__tier-label">{{ tier.tierLabel }}</div>
            <h3 class="vs-card__title">{{ plan.name }}</h3>
            <!-- Price -->
            <div class="vs-card__price-wrap">
              <span class="vs-card__price">{{ displayPrice(tier) }}</span>
              <span class="vs-card__currency">₪</span>
              <span class="vs-card__period">/ חודש</span>
            </div>
            <!-- Yearly savings row -->
            @if (billingPeriod === 'YEARLY') {
              <div class="vs-card__yearly-note">
                <span class="vs-card__yearly-total">{{ yearlyTotal(tier) }}₪ לשנה</span>
                @if (savingPct(tier) > 0) {
                  <span class="vs-card__yearly-badge">חסוך {{ savingPct(tier) }}%</span>
                }
              </div>
            }
            @if (billingPeriod === 'YEARLY' && tier.monthly) {
              <div class="vs-card__was">
                <s>{{ monthlyPrice(tier) }}₪</s> לחודש במחיר רגיל
              </div>
            }
            <!-- Features -->
            @if (features(tier).length) {
              <ul class="vs-card__features">
                @for (feat of features(tier); track feat) {
                  <li>
                    <i class="fas fa-check-circle feat-icon"></i>
                    <span>{{ feat }}</span>
                  </li>
                }
              </ul>
            }
            <!-- CTA -->
            <button
              class="vs-card__cta"
              [class.vs-card__cta--loading]="initiating === plan.id"
              [class.vs-card__cta--current]="tierAction(tier) === 'current'"
              [class.vs-card__cta--blocked]="tierAction(tier) === 'downgrade' || tierAction(tier) === 'renew-later'"
              [class.vs-card__cta--renew]="tierAction(tier) === 'renew'"
              [disabled]="ctaDisabled(tier)"
              (click)="subscribe(tier)"
              >
              @if (initiating === plan.id) {
                <i class="fas fa-circle-notch fa-spin"></i> מכין תשלום...
              } @else if (tierAction(tier) === 'current') {
                <i class="fas fa-check"></i> {{ ctaLabel(tier) }}
              } @else if (tierAction(tier) === 'downgrade') {
                <i class="fas fa-lock"></i> {{ ctaLabel(tier) }}
              } @else if (tierAction(tier) === 'upgrade') {
                <i class="fas fa-arrow-up"></i> {{ ctaLabel(tier) }}
              } @else if (tierAction(tier) === 'renew') {
                <i class="fas fa-redo-alt"></i> {{ ctaLabel(tier) }}
              } @else if (tierAction(tier) === 'renew-later') {
                <i class="fas fa-clock"></i> {{ ctaLabel(tier) }}
              } @else if (tierAction(tier) === 'switch-billing') {
                <i class="fas fa-exchange-alt"></i> {{ ctaLabel(tier) }}
              } @else {
                <i class="fas fa-arrow-left"></i> {{ ctaLabel(tier) }}
              }
            </button>
            @if (tierAction(tier) === 'renew' && paidUntil) {
              <div class="vs-card__renew-note">יתחיל ב-{{ paidUntil | date:'dd/MM/yyyy' }}, בתום המנוי הנוכחי</div>
            }
          }
        </div>
      }
    </div>
  }
  <!-- ── Empty state ────────────────────────────────────────────────────────── -->
  @if (tiers.length === 0) {
    <div class="vs-empty">
      <i class="fas fa-crown"></i>
      <p>החבילות יופיעו כאן בקרוב.</p>
      <small>ניתן להוסיף חבילות דרך פאנל הניהול.</small>
    </div>
  }
  <!-- ── Trust footer ───────────────────────────────────────────────────────── -->
  <div class="vs-footer-strip">
    <div class="vs-footer-strip__item"><i class="fas fa-shield-alt"></i><span>תשלום מאובטח</span></div>
    <div class="vs-footer-strip__item"><i class="fas fa-undo"></i><span>ביטול בכל עת</span></div>
    <div class="vs-footer-strip__item"><i class="fas fa-headset"></i><span>תמיכה 24/7</span></div>
    <div class="vs-footer-strip__item"><i class="fas fa-bolt"></i><span>הפעלה מיידית</span></div>
  </div>
}
